/* eslint-disable react/no-did-update-set-state */
import * as React from 'react';
import classnames from 'classnames';
import sanitizeHtml from 'sanitize-html';
import isEmpty from 'lodash/isEmpty';
import kebabCase from 'lodash/kebabCase';

import { FormField } from 'components/form';
import CharacterCount from 'components/character-count/CharacterCount';

import { __t, applyTranslations } from 'utils/language';
import {
  CHANNELS,
  IN_PROGRESS,
  MAX_NUM_ITEMS,
  NOT_FOUND,
  READY,
  splitChannels
} from 'modules/insights/reports/utils/queries/insights-reports-queries';

import './insights-reports-queries-modal-step-channels.scss';

import type {
  QueryChannel,
  QueryModalErrors,
  QueryModalTextFields
} from 'modules/insights/reports/types';

applyTranslations(require('modules/insights/reports/shared/insights-reports-shared.i18n.json'));
applyTranslations(require('modules/insights/reports/components/queries/modals/insights-reports-queries-modal.i18n.json'));

const SANITIZE_HTML_CONFIG_COMMON = {
  allowedTags: ['span', 'br'],
  allowedAttributes: {
    span: ['class']
  }
};

const SANITIZE_HTML_CONFIG_STRICT = {
  allowedTags: [],
  allowedAttributes: {}
};

type Props = {
  textFields: QueryModalTextFields,
  errors: QueryModalErrors,
  onChannelsFieldChange: (channels: Array<QueryChannel>) => void
};

type State = {
  isPasted: boolean
};

const SHARED_MSG_KEY = 'insights_reports_shared';
const MODAL_MSG_KEY = 'insights_reports_queries_modal';

class InsightsReportsQueriesModalStepChannels extends React.PureComponent<Props, State> {
  state = {
    isPasted: false
  };

  contentRef: React.RefObject<HTMLPreElement> = React.createRef();

  componentDidMount() {
    const contentRef = this.contentRef && this.contentRef.current;

    if (contentRef) {
      contentRef.focus();
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { isPasted } = this.state;
    const contentRef = this.contentRef && this.contentRef.current;

    // on paste, blur the field so the string parsing takes place
    if (isPasted !== prevState.isPasted && contentRef) {
      contentRef.blur();

      this.setState({
        isPasted: false
      });
    }
  }

  stringifyValues(values: Array<QueryChannel>): string {
    const labels = values.map(item => {
      if (item.status === READY || item.status === IN_PROGRESS) {
        return item.username;
      }

      return `<span class="insights-reports-queries-modal-step-channels__${kebabCase(item.status)}">${item.username}</span>`;
    });

    return `${labels.join('\n')}\n`;
  }

  handlePaste = (event: React.ClipboardEvent<HTMLElement>): void => {
    event.preventDefault();

    const text = event.clipboardData.getData('text/plain');
    const sanitizedText = sanitizeHtml(text, SANITIZE_HTML_CONFIG_STRICT);

    document.execCommand('insertHTML', false, sanitizedText);

    this.setState({
      isPasted: true
    });
  };

  handleUpdate = (event: React.SyntheticEvent<HTMLPreElement>): void => {
    const { textFields, onChannelsFieldChange } = this.props;
    const target = event.currentTarget;
    const value = target.innerText;
    const previousChannels = textFields[CHANNELS];
    const updatedChannels = splitChannels(value);

    onChannelsFieldChange(
      updatedChannels.filter(channel => !isEmpty(channel))
        .map(channel => ({
          username: channel,
          status: previousChannels.find(item => item.username === channel)?.status || IN_PROGRESS
        }))
    );
  };

  renderError(msgKey: string): React.ReactNode {
    return (
      <div className="insights-reports-queries-modal-step-channels__error">
        {__t(msgKey, {
          type: CHANNELS,
          max: MAX_NUM_ITEMS[CHANNELS]
        })}
      </div>
    );
  }

  renderErrors(): React.ReactNode {
    const { textFields, errors } = this.props;

    if (errors[CHANNELS]) {
      return isEmpty(textFields[CHANNELS])
        ? this.renderError(`${MODAL_MSG_KEY}.errors.empty`)
        : this.renderError(`${MODAL_MSG_KEY}.errors.count`);
    }

    return textFields[CHANNELS].some(channel => channel.status === NOT_FOUND)
      ? this.renderError(`${MODAL_MSG_KEY}.errors.${CHANNELS}`)
      : null;
  }

  renderCharacterCount(): React.ReactNode {
    const { textFields } = this.props;

    return (
      <div className="insights-reports-queries-modal-step-details__char-count">
        <CharacterCount
          charCount={textFields[CHANNELS].length}
          charLimit={MAX_NUM_ITEMS[CHANNELS]}
          labelOverride={__t(`${MODAL_MSG_KEY}.labels.${CHANNELS}_remaining`)}
          align="right"
          size="normal"
          highlightOnError
        />
      </div>
    );
  }

  renderField(): React.ReactNode {
    const { textFields } = this.props;
    const values = textFields[CHANNELS];
    const hasError = values.some(item => item.status === NOT_FOUND);

    const textareaClasses = classnames({
      'insights-reports-queries-modal-step-channels__values': true,
      'insights-reports-queries-modal-step-channels__values--has-error': hasError
    });

    return (
      <pre
        key={values.map(item => item.username).join('')}
        ref={this.contentRef}
        tabIndex={-1} // allows element to be focused via script
        onBlur={this.handleUpdate}
        onPaste={this.handlePaste}
        className={textareaClasses}
        contentEditable
        data-placeholder={__t(`${MODAL_MSG_KEY}.placeholders.${CHANNELS}`)}
        dangerouslySetInnerHTML={{
          __html: sanitizeHtml(this.stringifyValues(values), SANITIZE_HTML_CONFIG_COMMON)
        }}
      />
    );
  }

  renderFormField(): React.ReactNode {
    return (
      <FormField
        direction="column"
        label={__t(`${MODAL_MSG_KEY}.labels.${CHANNELS}`)}
        labelType="highlighted"
        fontSize="small"
        tooltipMessage={__t(`${SHARED_MSG_KEY}.tooltips.${CHANNELS}`)}
        showValidationIcon={false}
        showRequiredMark
      >
        {this.renderField()}
        <div className="insights-reports-queries-modal-step-channels__messages">
          {this.renderErrors()}
          {this.renderCharacterCount()}
        </div>
      </FormField>
    );
  }
  render(): React.ReactNode {
    return (
      <div className="insights-reports-queries-modal-step-channels">
        {this.renderFormField()}
      </div>
    );
  }
}

export default InsightsReportsQueriesModalStepChannels;
